import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import API from '../lib/api';
import ListingCard from '../components/ListingCard';
import { BrowseSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Search, SlidersHorizontal, X, Car, Loader2, Crown, ChevronRight, Sparkles, Wrench } from 'lucide-react';

const MAKES = ['Toyota', 'Honda', 'Nissan', 'Hyundai', 'Kia', 'Mercedes-Benz', 'BMW', 'Volkswagen', 'Ford', 'Chevrolet', 'Genesis', 'SsangYong', 'Mazda', 'Subaru', 'Mitsubishi', 'Suzuki', 'Peugeot', 'Renault', 'Volvo', 'Audi', 'Porsche', 'Land Rover', 'Jaguar', 'Lexus', 'BYD', 'Geely', 'Great Wall', 'Chery', 'MG', 'Tata', 'Mahindra', 'Skoda', 'Dacia', 'Tesla'];
const LOCATIONS = ['All', 'Accra', 'Kumasi', 'Tamale', 'Cape Coast', 'Takoradi', 'Tema', 'Ho', 'Sunyani'];
const PART_CATEGORIES = [
  "All Categories",
  "Engine & Drivetrain",
  "Body & Exterior",
  "Electrical & Lighting",
  "Suspension & Steering",
  "Brakes",
  "Interior & Comfort",
  "Wheels & Tires",
  "Transmission",
  "Cooling System",
  "Exhaust System",
  "Filters & Fluids",
  "AC & Heating",
  "Accessories"
];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'trust', label: 'Trust Score' },
];

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [searching, setSearching] = useState(false);
  const [listingType, setListingType] = useState(searchParams.get('type') || 'all'); // 'all', 'car', 'part'
  const [filters, setFilters] = useState({
    make: searchParams.get('make') || '',
    location: searchParams.get('location') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    sort: searchParams.get('sort') || 'newest',
    part_category: searchParams.get('part_category') || '',
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchFeaturedListings();
    fetchListings();
  }, [page, filters.sort, listingType]);

  const fetchFeaturedListings = async () => {
    try {
      const res = await API.get('/listings/featured-dealers', { params: { limit: 6 } });
      setFeaturedListings(res.data.listings || []);
    } catch (err) {
      console.error('Error fetching featured listings:', err);
    }
  };

  const fetchListings = async (searchTerm = null) => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sort: filters.sort };
      if (searchTerm || searchQuery) params.search = searchTerm || searchQuery;
      if (listingType && listingType !== 'all') params.listing_type = listingType;
      if (filters.make) params.make = filters.make;
      if (filters.location && filters.location !== 'All') params.location = filters.location;
      if (filters.min_price) params.min_price = parseFloat(filters.min_price);
      if (filters.max_price) params.max_price = parseFloat(filters.max_price);
      if (filters.part_category && filters.part_category !== 'All Categories') params.part_category = filters.part_category;
      const res = await API.get('/listings', { params });
      const listingsData = Array.isArray(res.data?.listings) ? res.data.listings : [];
      setListings(listingsData);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error('Error fetching listings:', err);
      setListings([]);
      setTotal(0);
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearching(true);
    fetchListings(searchQuery).finally(() => setSearching(false));
  };

  const clearSearch = () => {
    setSearchQuery('');
    setPage(1);
    fetchListings('');
  };

  const applyFilters = () => {
    setPage(1);
    fetchListings();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ make: '', location: '', min_price: '', max_price: '', sort: 'newest', part_category: '' });
    setPage(1);
    setTimeout(fetchListings, 0);
  };

  const handleTypeChange = (type) => {
    setListingType(type);
    setFilters(f => ({ ...f, make: '', part_category: '' }));
    setPage(1);
  };

  return (
    <main data-testid="browse-page" className="min-h-screen bg-background">
      <BrowseSEO filters={{ listing_type: listingType, make: filters.make, location: filters.location }} />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Search Bar */}
        <div className="mb-6">
          <form onSubmit={handleSearch} className="flex gap-2 max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by make, model, location, or keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 h-11"
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button type="submit" className="bg-gold text-black hover:brightness-110 h-11 px-6" disabled={searching} data-testid="search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </form>
          {searchQuery && (
            <p className="text-sm text-muted-foreground mt-2">
              Showing results for "<span className="text-foreground font-medium">{searchQuery}</span>"
            </p>
          )}
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">
              {listingType === 'part' ? 'Browse Spare Parts' : listingType === 'car' ? 'Browse Cars' : 'Browse Marketplace'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {total} {total === 1 ? 'listing' : 'listings'} in Ghana
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2" onClick={() => setShowFilters(!showFilters)} data-testid="filter-toggle">
              <SlidersHorizontal className="w-4 h-4" /> Filters
            </Button>
            <Select value={filters.sort} onValueChange={(v) => setFilters(f => ({ ...f, sort: v }))}>
              <SelectTrigger className="w-44" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Listing Type Tabs */}
        <div className="flex gap-2 mb-6" data-testid="listing-type-tabs">
          <button
            onClick={() => handleTypeChange('all')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              listingType === 'all' ? 'bg-gold text-black' : 'bg-muted hover:bg-muted/80'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleTypeChange('car')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              listingType === 'car' ? 'bg-gold text-black' : 'bg-muted hover:bg-muted/80'
            }`}
            data-testid="tab-cars"
          >
            <Car className="w-4 h-4" /> Cars
          </button>
          <button
            onClick={() => handleTypeChange('part')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              listingType === 'part' ? 'bg-gold text-black' : 'bg-muted hover:bg-muted/80'
            }`}
            data-testid="tab-parts"
          >
            <Wrench className="w-4 h-4" /> Spare Parts
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-card border border-border/50 rounded-xl p-5 mb-8" data-testid="filters-panel">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {/* Make filter - for cars */}
              {listingType !== 'part' && (
                <Select value={filters.make} onValueChange={(v) => setFilters(f => ({ ...f, make: v }))}>
                  <SelectTrigger data-testid="filter-make"><SelectValue placeholder="Make" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_makes">All Makes</SelectItem>
                    {MAKES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              
              {/* Part Category filter - for parts */}
              {listingType === 'part' && (
                <Select value={filters.part_category} onValueChange={(v) => setFilters(f => ({ ...f, part_category: v }))}>
                  <SelectTrigger data-testid="filter-part-category"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    {PART_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              
              {/* Compatible Make filter - for parts */}
              {listingType === 'part' && (
                <Select value={filters.make} onValueChange={(v) => setFilters(f => ({ ...f, make: v }))}>
                  <SelectTrigger data-testid="filter-compatible-make"><SelectValue placeholder="Compatible Make" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_makes">All Makes</SelectItem>
                    {MAKES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              
              <Select value={filters.location} onValueChange={(v) => setFilters(f => ({ ...f, location: v }))}>
                <SelectTrigger data-testid="filter-location"><SelectValue placeholder="Location" /></SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Min Price (GHS)" type="number" value={filters.min_price}
                onChange={(e) => setFilters(f => ({ ...f, min_price: e.target.value }))} data-testid="filter-min-price" />
              <Input placeholder="Max Price (GHS)" type="number" value={filters.max_price}
                onChange={(e) => setFilters(f => ({ ...f, max_price: e.target.value }))} data-testid="filter-max-price" />
              <div className="flex gap-2">
                <Button onClick={applyFilters} className="bg-gold text-black hover:brightness-110 flex-1" data-testid="apply-filters-btn">
                  <Search className="w-4 h-4 mr-1" /> Apply
                </Button>
                <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters-btn"><X className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        )}

        {/* Featured Dealer Listings */}
        {featuredListings.length > 0 && page === 1 && !searchQuery && (
          <div className="mb-10" data-testid="featured-dealer-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    Featured Dealer Listings
                    <Badge className="bg-gold/20 text-gold border-none text-xs">
                      <Sparkles className="w-3 h-3 mr-1" /> Premium
                    </Badge>
                  </h2>
                  <p className="text-xs text-muted-foreground">Verified dealers with unlimited inventory</p>
                </div>
              </div>
              <Link to="/dealer">
                <Button variant="ghost" size="sm" className="text-gold gap-1 text-xs">
                  Become a Dealer <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuredListings.map(l => (
                <div key={l.listing_id} className="relative">
                  <div className="absolute top-3 left-3 z-10">
                    <Badge className="bg-gold text-black border-none gap-1 text-xs shadow-lg">
                      <Crown className="w-3 h-3" /> Dealer
                    </Badge>
                  </div>
                  <ListingCard listing={l} />
                </div>
              ))}
            </div>
            <div className="border-b border-border/50 mt-10 mb-6" />
          </div>
        )}

        {/* All Listings Header */}
        {featuredListings.length > 0 && page === 1 && !searchQuery && (
          <h2 className="text-lg font-semibold mb-4">All Listings</h2>
        )}

        {/* Listings Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-card border border-border/50 rounded-xl overflow-hidden">
                <div className="aspect-[16/10] bg-muted skeleton-pulse" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-muted rounded skeleton-pulse w-3/4" />
                  <div className="h-6 bg-muted rounded skeleton-pulse w-1/2" />
                  <div className="h-3 bg-muted rounded skeleton-pulse w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <Car className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-1">No listings found</h3>
            <p className="text-sm text-muted-foreground">Try adjusting your filters or check back later.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="listings-grid">
            {listings.map((l, i) => (
              <motion.div
                key={l.listing_id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.3), ease: 'easeOut' }}
              >
                <ListingCard listing={l} />
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="flex justify-center gap-2 mt-10">
            <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">Previous</Button>
            <span className="flex items-center px-4 text-sm text-muted-foreground">Page {page}</span>
            <Button variant="outline" disabled={listings.length < 20} onClick={() => setPage(p => p + 1)} data-testid="next-page">Next</Button>
          </div>
        )}
      </div>
    </main>
  );
}
